import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, BookOpen, Clock, Star, Trophy, Zap, Shield, Target, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ROUTE_PATHS } from '@/lib/index';
import { creditHacks } from '@/lib/dashboard-data';
import { springPresets } from '@/lib/motion';

export default function CreditEducation() {
  const learningModules = [
    {
      id: '1',
      title: 'Credit Basics 101',
      description: 'Understanding credit scores, reports, and how they impact your financial life',
      duration: '15 min',
      lessons: 5,
      level: 'Beginner',
    },
    {
      id: '2',
      title: 'Utilization Mastery',
      description: 'Master the 30% rule, payment timing, and advanced utilization strategies',
      duration: '12 min',
      lessons: 4,
      level: 'Intermediate',
    },
    {
      id: '3',
      title: 'Dispute Law (FCRA/FDCPA)',
      description: 'Your legal rights under federal law and how to use them effectively',
      duration: '20 min',
      lessons: 6,
      level: 'Advanced',
    },
    {
      id: '4',
      title: 'Business Credit Blueprint',
      description: 'Build business credit separate from personal, access funding faster',
      duration: '18 min',
      lessons: 5,
      level: 'Intermediate',
    },
    {
      id: '5',
      title: 'Mortgage Preparation',
      description: 'Everything you need to qualify for a home loan and get the best rate',
      duration: '25 min',
      lessons: 7,
      level: 'Advanced',
    },
    {
      id: '6',
      title: '0% Interest Funding',
      description: 'Access $50k-$150k in interest-free capital through strategic card stacking',
      duration: '22 min',
      lessons: 6,
      level: 'Advanced',
    },
  ];

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Intermediate':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'Advanced':
        return 'bg-chart-4/10 text-chart-4 border-chart-4/20';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Link to={ROUTE_PATHS.DASHBOARD}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Credit Education Module</h1>
              <p className="text-muted-foreground mt-1">Learn credit hacks, strategies, and insider tips</p>
            </div>
          </div>
          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
            {[
              { label: 'Lessons Completed', value: '3/12', icon: BookOpen },
              { label: 'Points Earned', value: '240 XP', icon: Star },
              { label: 'Score Impact', value: '+94 pts', icon: TrendingUp },
              { label: 'Time Invested', value: '2.5 hrs', icon: Clock },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="flex items-center gap-3 bg-muted/30 rounded-lg px-4 py-3">
                  <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-sm font-bold text-primary">{stat.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-12">
        {/* Quick Credit Hacks */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Quick Credit Hacks</h2>
              <p className="text-sm text-muted-foreground">Fast wins to boost your score immediately</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {creditHacks.map((hack, index) => (
              <motion.div
                key={hack.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: index * 0.1 }}
              >
                <Card className="p-6 h-full hover:border-primary/50 transition-all group cursor-pointer">
                  <div className="flex items-start justify-between mb-4">
                    <Badge
                      className={
                        hack.difficulty === 'Easy'
                          ? 'bg-primary/10 text-primary border-primary/20'
                          : hack.difficulty === 'Medium'
                          ? 'bg-accent/10 text-accent border-accent/20'
                          : 'bg-destructive/10 text-destructive border-destructive/20'
                      }
                    >
                      {hack.difficulty}
                    </Badge>
                    <Badge variant="outline" className="border-primary text-primary font-mono">
                      {hack.impact}
                    </Badge>
                  </div>
                  <h4 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">{hack.title}</h4>
                  <p className="text-sm text-muted-foreground">{hack.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Learning Modules */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Learning Modules</h2>
              <p className="text-sm text-muted-foreground">Deep-dive courses to master credit and funding</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {learningModules.map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: index * 0.1 }}
              >
                <Card className="p-6 h-full hover:border-accent/50 transition-all group cursor-pointer">
                  <Badge className={getLevelColor(module.level)} variant="outline">
                    {module.level}
                  </Badge>
                  <h4 className="text-lg font-semibold mt-4 mb-2 group-hover:text-accent transition-colors">
                    {module.title}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">{module.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {module.duration}
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {module.lessons} lessons
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="w-full mt-4 group-hover:bg-accent/10">
                    Start Learning
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Your Progress */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Your Learning Progress</h2>
              <p className="text-sm text-muted-foreground">Track your journey to financial mastery</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { label: 'Credit Basics', progress: 80, status: 'In Progress', color: 'text-primary' },
              { label: 'Dispute Law (FCRA)', progress: 40, status: 'In Progress', color: 'text-primary' },
              { label: 'Utilization Mastery', progress: 100, status: 'Completed', color: 'text-accent' },
              { label: 'Business Credit', progress: 0, status: 'Locked', color: 'text-muted-foreground' },
              { label: 'Mortgage Prep', progress: 0, status: 'Locked', color: 'text-muted-foreground' },
              { label: '0% Funding Strategy', progress: 0, status: 'Locked', color: 'text-muted-foreground' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: index * 0.08 }}
              >
                <Card className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-sm">{item.label}</span>
                    <Badge
                      variant="outline"
                      className={
                        item.status === 'Completed' ? 'border-primary text-primary' :
                        item.status === 'In Progress' ? 'border-accent text-accent' :
                        'border-border text-muted-foreground'
                      }
                    >
                      {item.status === 'Completed' && <Star className="w-3 h-3 mr-1" />}
                      {item.status}
                    </Badge>
                  </div>
                  <Progress value={item.progress} className="h-2 mb-2" />
                  <p className="text-xs text-muted-foreground">{item.progress}% complete</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Wealth Building Bonus */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Wealth Building Strategies</h2>
              <p className="text-sm text-muted-foreground">Turn your credit score into a wealth-building tool</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Zap,
                title: 'The 800+ Score Blueprint',
                description: 'Once you hit 740+, here\'s exactly how to push to 800 and unlock elite credit card offers, lowest mortgage rates, and $250k+ in business funding.',
                tag: 'Advanced',
                color: 'text-primary',
                bg: 'bg-primary/10',
              },
              {
                icon: Shield,
                title: 'Credit Card Stacking Method',
                description: 'How to apply for multiple 0% APR cards in a single day to access $50k-$150k in interest-free capital for investing or business launch.',
                tag: 'Funding',
                color: 'text-accent',
                bg: 'bg-accent/10',
              },
              {
                icon: Target,
                title: 'The FICO Optimization System',
                description: 'Understanding the exact algorithms used by each bureau so you can strategically time payments, new accounts, and balance reporting.',
                tag: 'Tactics',
                color: 'text-primary',
                bg: 'bg-primary/10',
              },
              {
                icon: TrendingUp,
                title: 'From Credit Repair to Net Worth',
                description: 'A complete roadmap showing how 6,000+ clients went from 580 credit scores to $500k+ net worth using our Credit To Millions system.',
                tag: 'Roadmap',
                color: 'text-accent',
                bg: 'bg-accent/10',
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full hover:border-primary/40 transition-all group cursor-pointer">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${item.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold group-hover:text-primary transition-colors">{item.title}</h4>
                          <Badge variant="outline" className="text-xs">{item.tag}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                        <Button size="sm" variant="ghost" className="mt-3 p-0 h-auto text-primary hover:text-primary/80">
                          Unlock Strategy →
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}